import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Heart, User, LifeBuoy, LogOut, BookOpen } from 'lucide-react';

export default function MobileSidebar({ open, onClose, navLinks, user, onLogout }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm lg:hidden"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
            className="fixed right-0 top-0 z-[70] h-full w-[82%] max-w-sm bg-white dark:bg-surface-dark p-6 shadow-soft-lg lg:hidden overflow-y-auto [scrollbar-width:none]"
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-lg font-semibold text-slate-900 dark:text-white">Menu</span>
              <button onClick={onClose} aria-label="Close menu" className="rounded-full p-2 hover:bg-slate-100 dark:hover:bg-white/10">
                <X size={20} />
              </button>
            </div>

            {user && (
              <div className="mt-6 flex items-center gap-3 rounded-xl bg-primary-50 dark:bg-primary-900/20 p-4">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-primary text-white font-semibold">
                    {user.name?.charAt(0)}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">{user.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user.email}</p>
                </div>
              </div>
            )}

            <nav className="mt-6 flex flex-col gap-1">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={onClose}
                  className="rounded-xl px-3 py-3 text-base font-medium text-slate-700 dark:text-slate-200 hover:bg-primary-50 dark:hover:bg-white/5"
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            <div className="mt-6 h-px bg-slate-100 dark:bg-white/10" />

            <div className="mt-6 flex flex-col gap-1">
              {[
                { label: 'My Account', to: '/account', icon: User },
                { label: 'Wishlist', to: '/account/wishlist', icon: Heart },
                { label: 'Support', to: '/support', icon: LifeBuoy },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={onClose}
                  className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-primary-50 dark:hover:bg-white/5"
                >
                  <l.icon size={17} /> {l.label}
                </Link>
              ))}
              {user && (user.role === 'author' || user.pendingAuthorUpgrade) && (
                <Link to="/publish/dashboard" onClick={onClose} className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-primary-50 dark:hover:bg-white/5">
                  <BookOpen size={17} /> Author Dashboard
                </Link>
              )}
            </div>

            <div className="mt-6 h-px bg-slate-100 dark:bg-white/10" />

            {user ? (
              <button
                onClick={() => { onLogout(); onClose(); }}
                className="mt-6 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10"
              >
                <LogOut size={17} /> Sign Out
              </button>
            ) : (
              <Link to="/login" onClick={onClose} className="btn-primary mt-6 w-full justify-center">
                Sign In
              </Link>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
